import { EditorFeature, EditorFeatureProperties } from "common/utils/mapEditor";
import { FarmFeature, FarmFeatureProperties } from "common/utils/farm";
import { Feature } from "geojson";
import { JobFeature, JobFeatureProperties } from "common/utils/rtcJobs";
import React from "react";

export enum MapImageId {
  GoToAndFace = "go-to-and-face",
  GpsPoint = "gps-point",
  RtkFixed = "rtk-fixed",
  RtkFloat = "rtk-float",
  TextureInProgress = "texture-in-progress",
  NoTexture = "texture-none",
}

/**
 * a MapImageResolver is a function that takes a MapImageId and can produce
 * a URL that Mapbox can XHR to load it.
 */
export type MapImageResolver = (id: MapImageId) => string;

export interface LayerConfig<T> {
  interactionLayers: InteractionLayer[];
  idsToLoad: MapImageId[];
  Layers: React.FC<T>;
}

export interface InteractionLayer {
  id: string;
  cursor?: string;
  /** longitude/latitude are where the click landed, when the map provides it */
  onClick?: (
    feature?: PortalFeature,
    longitude?: number,
    latitude?: number
  ) => void;
  onEnter?: (feature?: PortalFeature) => void;
  onLeave?: (feature?: PortalFeature) => void;
  onMouseMove?: (feature?: PortalFeature) => void;
  deferClick?: boolean; // hold onClick so a double-click-to-zoom doesn't also act
  onDragStart?: (feature: PortalFeature) => boolean | void;
  onDrag?: (longitude: number, latitude: number) => void;
  onDragEnd?: (longitude: number, latitude: number) => void;
}

/**
 * slot layer IDs to provide well-defined insertion points in the layer stack
 * for different parts of the autotractor map decoration.  ordered from back
 * to front in rendering order; content that renders in the farms slot should
 * have beforeId={AutotractorSlotIDs.FARMS}, etc.
 */
export enum AutotractorSlotIDs {
  FARMS = "autotractor-farms",
  JOB_OBJECTIVES = "autotractor-job-objectives",
  JOB_TASKS = "autotractor-job-tasks",
  OBSTACLES = "autotractor-obstacles",
  PIVOTS = "autotractor-pivots",
  INTERACTIVE_OBJECTS = "autotractor-interactive-objects",
}

export type AutotractorProps = FarmFeatureProperties | JobFeatureProperties;

export type EditorProps = EditorFeatureProperties;

export enum AutotractorPropsType {
  POINT = "point",
  ZONE = "zone",
  PIVOT = "pivot",
  TRACTOR = "tractor",
  OBJECTIVE = "objective",
  TASK = "task",
}

export enum EditorPropsType {
  GHOST_POINT = "ghost_point",
  PLANNED_PATH = "planned_path",
  POINT = "point",
  INDEXED_POINT = "indexed_point",
  DRAFT_POINT = "draft_point",
}

export interface AutotractorBaseProps extends PortalFeatureBaseProps {
  domain: Domain.AUTOTRACTOR;
  type: AutotractorPropsType;
}

export enum Domain {
  AUTOTRACTOR = "at",
  LASERWEEDER = "lw",
  EDITOR = "ed",
}

export interface PortalFeatureBaseProps {
  __portalFeatureProps: true;
  domain: Domain;
}

export interface EditorBaseProps extends PortalFeatureBaseProps {
  domain: Domain.EDITOR;
  type: EditorPropsType;
}

export type AutotractorFeature = FarmFeature | JobFeature | EditorFeature;

export type PortalFeature = AutotractorFeature;

export type PortalFeatureProps = AutotractorProps | EditorProps;

export const arePortalFeatureProps = (x?: unknown): x is PortalFeatureProps => {
  return (x as any)?.__portalFeatureProps === true;
};

export const isPortalFeature = (x?: Feature): x is PortalFeature => {
  const props = arePortalFeatureProps(x?.properties);
  return Boolean(x && props);
};
